import Order from 'App/resources/Model/Order';
import translate from 'Components/Helpers/translate';
import type { OpportunityRiskSettings } from 'App/resources/AllIWant';

export enum ForecastingRiskLevel {
	Medium = 'Medium',
	High = 'High'
}

export class ForecastingRisk {
	field: keyof Order['risks'];
	paramKey: keyof OpportunityRiskSettings;
	type: ForecastingRiskLevel;
	langOpts?: (order: Order) => Object;
	extraRequirement?: (order: Order) => boolean;

	constructor(config: Pick<ForecastingRisk, 'field' | 'paramKey' | 'type' | 'langOpts' | 'extraRequirement'>) {
		const { field, paramKey, type, langOpts, extraRequirement } = config;
		this.field = field;
		this.paramKey = paramKey;
		this.type = type;
		this.langOpts = langOpts;
		this.extraRequirement = extraRequirement;
	}

	hasRisk(order: Order) {
		const settings = Tools.AppService.getMetadata().params.OpportunityRiskSettings ?? {};
		return !!order.risks?.[this.field] && settings[this.paramKey] && (this.extraRequirement?.(order) ?? true);
	}

	tooltip(order: Order) {
		return translate(`forecastingRisk.${this.field}`, this.langOpts?.(order));
	}
}

export const ForecastingRisks: ForecastingRisk[] = [
	new ForecastingRisk({
		field: 'noNextStepOccured',
		type: ForecastingRiskLevel.High,
		paramKey: 'noNextStep',
		langOpts: () => ({})
	}),
	new ForecastingRisk({
		field: 'noAppointmentsOccured',
		type: ForecastingRiskLevel.High,
		paramKey: 'noAppointments',
		langOpts: order => ({
			statCount: order.userSalesStatistics?.mostCommonAppointmentsPerOrder,
			orderCount: order.noCompletedAppointments
		})
	}),
	new ForecastingRisk({
		field: 'postponedAppointmentsOccured',
		type: ForecastingRiskLevel.High,
		paramKey: 'postponedAppointments',
		langOpts: order => ({ count: order.noPostponedAppointments })
	}),
	new ForecastingRisk({
		field: 'closingDateChangedOccured',
		paramKey: 'closingDateChanged',
		type: ForecastingRiskLevel.High,
		langOpts: order => ({ count: order.noTimesClosingDateChanged }),
		extraRequirement: order => order.probability >= 50
	}),
	new ForecastingRisk({
		field: 'notAnsweringCallsOccured',
		paramKey: 'notAnsweringCalls',
		type: ForecastingRiskLevel.High,
		langOpts: order => ({ count: order.noTimesCallsNotAnswered })
	}),
	new ForecastingRisk({
		field: 'orderValueOccured',
		paramKey: 'orderValue',
		type: ForecastingRiskLevel.High
	}),
	new ForecastingRisk({
		field: 'closingDateChangedOccured',
		paramKey: 'closingDateChanged',
		type: ForecastingRiskLevel.Medium,
		langOpts: order => ({ count: order.noTimesClosingDateChanged }),
		extraRequirement: order => order.probability < 50
	}),
	new ForecastingRisk({
		field: 'orderValueChangedOccured',
		paramKey: 'orderValueChanged',
		type: ForecastingRiskLevel.Medium,
		langOpts: order => ({ count: order.noTimesOrderValueChanged })
	}),
	new ForecastingRisk({
		field: 'noRecentContactOccured',
		paramKey: 'noRecentContact',
		type: ForecastingRiskLevel.Medium,
		langOpts: () => ({})
	}),
	new ForecastingRisk({
		field: 'companySizeOccured',
		paramKey: 'companySize',
		type: ForecastingRiskLevel.Medium
	})
];

export const getOrderForecastingRisk = (order: Order) => ForecastingRisks.find(risk => risk.hasRisk(order)) ?? null;
